import { useMutation } from "@apollo/client";
import Label from "@mui/icons-material/Label";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  ChangeCohortMemberStatusFormDialog,
  ChangeStatusFormState,
} from "components/ChangeCohortMemberStatusFormDialog";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { useBulkActionContext } from ".";

export function ChangeStatusMenuItem() {
  const { setDialogState, selectedRows } = useBulkActionContext();

  return (
    <MenuItem
      onClick={() => setDialogState("changeStatus")}
      disabled={selectedRows.map((row) => row.id.length === 0).length === 0}
    >
      <ListItemIcon>
        <Label />
      </ListItemIcon>
      <ListItemText>Change Status</ListItemText>
    </MenuItem>
  );
}

export const BULK_UPDATE_COHORT_MEMBER_STATUS_MUTATION = gql(`
  mutation BulkUpdateCohortMemberStatus($cohortId: ID!, $cohortMemberIds: [ID!]!, $status: CohortMemberStatus!) {
    bulkUpdateCohortMemberStatus(cohortId: $cohortId, cohortMemberIds: $cohortMemberIds, status: $status)
  }
`);

export function ChangeStatusDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();
  const cohortMemberIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);

  const cohortId = useMemo(() => selectedRows[0]?.cohortId, [selectedRows]);

  const [mutate, { loading }] = useMutation(BULK_UPDATE_COHORT_MEMBER_STATUS_MUTATION, {
    refetchQueries: ["CohortMembersTable", "GetCohort"],
  });

  const submit = useCallback(
    async (formState: ChangeStatusFormState) => {
      try {
        await mutate({
          variables: {
            cohortId,
            cohortMemberIds,
            status: formState.status,
          },
        });
        alerts.success(
          `Successfully changed the status of ${cohortMemberIds.length} ${pluralize(
            "Cohort Member",
            cohortMemberIds.length
          )} to ${formState.status.replace(/_/g, " ")}`
        );
        onClose();
      } catch (err) {
        alerts.error("An error occurred while changing the status", err);
      }
    },
    [alerts, mutate, cohortMemberIds, cohortId, onClose]
  );

  return (
    <ChangeCohortMemberStatusFormDialog
      cohortMemberIds={cohortMemberIds}
      open={dialogState === "changeStatus"}
      onClose={onClose}
      onSubmit={submit}
      loading={loading}
    />
  );
}
